import pptxgen from "pptxgenjs";

// OFUESI Brand Colors
const COLORS = {
  primaryBlue: "1a3a5f",
  secondaryGold: "eab308",
  white: "FFFFFF",
  darkText: "1f2937",
  lightGray: "f3f4f6",
  green: "22c55e",
  red: "ef4444",
};

// Slide master configuration
const configureMaster = (pptx: pptxgen) => {
  pptx.defineSlideMaster({
    title: "OFUESI_MASTER",
    background: { color: COLORS.white },
    objects: [
      { rect: { x: 0, y: 0, w: "100%", h: 0.6, fill: { color: COLORS.primaryBlue } } },
      { 
        text: { 
          text: "OFUESI", 
          options: { x: 0.3, y: 0.15, w: 2, h: 0.3, fontSize: 14, bold: true, color: COLORS.white } 
        } 
      },
      { rect: { x: 0, y: 5.2, w: "100%", h: 0.3, fill: { color: COLORS.secondaryGold } } },
    ],
  });

  pptx.defineSlideMaster({
    title: "TITLE_MASTER",
    background: { color: COLORS.primaryBlue },
  });
};

// Helper function to add a section header
const addSectionSlide = (pptx: pptxgen, title: string, subtitle?: string) => {
  const slide = pptx.addSlide({ masterName: "TITLE_MASTER" });
  slide.addText(title, {
    x: 0.5,
    y: 2,
    w: 9,
    h: 1,
    fontSize: 36,
    bold: true,
    color: COLORS.white,
    align: "center",
  });
  if (subtitle) {
    slide.addText(subtitle, {
      x: 0.5,
      y: 3,
      w: 9,
      h: 0.5,
      fontSize: 18,
      color: COLORS.secondaryGold,
      align: "center",
    });
  }
  return slide;
};

// Slide 1: Title
const addTitleSlide = (pptx: pptxgen) => {
  const slide = pptx.addSlide({ masterName: "TITLE_MASTER" });
  
  slide.addText("OFUESI", {
    x: 0.5,
    y: 1.5,
    w: 9,
    h: 0.8,
    fontSize: 48,
    bold: true,
    color: COLORS.secondaryGold,
    align: "center",
  });
  
  slide.addText("Website Project Report", {
    x: 0.5,
    y: 2.3,
    w: 9,
    h: 0.6,
    fontSize: 32,
    color: COLORS.white,
    align: "center",
  });
  
  slide.addText("Development Cost & Platform Comparison Analysis", {
    x: 0.5,
    y: 3,
    w: 9,
    h: 0.5,
    fontSize: 18,
    color: COLORS.white,
    align: "center",
  });
  
  const today = new Date().toLocaleDateString("en-US", { 
    year: "numeric", 
    month: "long", 
    day: "numeric" 
  });
  
  slide.addText(today, {
    x: 0.5,
    y: 4.5,
    w: 9,
    h: 0.4,
    fontSize: 14,
    color: COLORS.white,
    align: "center",
  });
};

// Slide 2: Executive Summary
const addExecutiveSummary = (pptx: pptxgen) => {
  const slide = pptx.addSlide({ masterName: "OFUESI_MASTER" });
  
  slide.addText("Executive Summary", {
    x: 0.5,
    y: 0.8,
    w: 9,
    h: 0.5,
    fontSize: 28,
    bold: true,
    color: COLORS.primaryBlue,
  });
  
  const summaryData = [
    { label: "Total Investment", value: "$1,000 one-time + $100/month", color: COLORS.primaryBlue },
    { label: "Market Value of Features", value: "$13,000 - $33,000", color: COLORS.green },
    { label: "Estimated Savings", value: "$12,000 - $32,000", color: COLORS.secondaryGold },
    { label: "Development Time Saved", value: "3-6 months", color: COLORS.primaryBlue },
  ];
  
  summaryData.forEach((item, idx) => {
    slide.addShape(pptx.ShapeType.roundRect, {
      x: 0.5,
      y: 1.5 + idx * 0.9,
      w: 9,
      h: 0.75,
      fill: { color: COLORS.lightGray },
      line: { color: item.color, width: 2 },
    });
    
    slide.addText(item.label, {
      x: 0.7,
      y: 1.55 + idx * 0.9,
      w: 4,
      h: 0.35,
      fontSize: 14,
      color: COLORS.darkText,
    });
    
    slide.addText(item.value, {
      x: 5,
      y: 1.55 + idx * 0.9,
      w: 4.3,
      h: 0.35,
      fontSize: 16,
      bold: true,
      color: item.color,
      align: "right",
    });
  });
  
  slide.addText("This report provides a comprehensive comparison of development costs, hosting expenses, features, and security capabilities between traditional platforms and the custom OFUESI solution.", {
    x: 0.5,
    y: 4.6,
    w: 9,
    h: 0.5,
    fontSize: 11,
    color: COLORS.darkText,
    align: "center",
  });
};

// Slide 3: Project Statistics
const addProjectStatistics = (pptx: pptxgen) => {
  const slide = pptx.addSlide({ masterName: "OFUESI_MASTER" });
  
  slide.addText("Project Statistics", {
    x: 0.5,
    y: 0.8,
    w: 9,
    h: 0.5,
    fontSize: 28,
    bold: true,
    color: COLORS.primaryBlue,
  });
  
  const stats = [
    { label: "Database Tables", value: "36", icon: "📊" },
    { label: "Database Migrations", value: "59", icon: "🔄" },
    { label: "Edge Functions", value: "18", icon: "⚡" },
    { label: "Public Pages", value: "16", icon: "📄" },
    { label: "Admin Components", value: "34", icon: "🛠️" },
    { label: "UI Components", value: "45+", icon: "🎨" },
  ];
  
  stats.forEach((stat, idx) => {
    const col = idx % 3;
    const row = Math.floor(idx / 3);
    
    slide.addShape(pptx.ShapeType.roundRect, {
      x: 0.5 + col * 3.1,
      y: 1.5 + row * 1.6,
      w: 2.9,
      h: 1.4,
      fill: { color: COLORS.lightGray },
      line: { color: COLORS.primaryBlue, width: 1 },
    });
    
    slide.addText(stat.icon, {
      x: 0.5 + col * 3.1,
      y: 1.55 + row * 1.6,
      w: 2.9,
      h: 0.4,
      fontSize: 24,
      align: "center",
    });
    
    slide.addText(stat.value, {
      x: 0.5 + col * 3.1,
      y: 1.95 + row * 1.6,
      w: 2.9,
      h: 0.5,
      fontSize: 28,
      bold: true,
      color: COLORS.primaryBlue,
      align: "center",
    });
    
    slide.addText(stat.label, {
      x: 0.5 + col * 3.1,
      y: 2.45 + row * 1.6,
      w: 2.9,
      h: 0.3,
      fontSize: 11,
      color: COLORS.darkText,
      align: "center",
    });
  });
};

// Slide 4: Live Data Metrics
const addLiveDataMetrics = (pptx: pptxgen) => {
  const slide = pptx.addSlide({ masterName: "OFUESI_MASTER" });
  
  slide.addText("Live Data Metrics", {
    x: 0.5,
    y: 0.8,
    w: 9,
    h: 0.5,
    fontSize: 28,
    bold: true,
    color: COLORS.primaryBlue,
  });
  
  slide.addText("Current data stored in the OFUESI database", {
    x: 0.5,
    y: 1.3,
    w: 9,
    h: 0.3,
    fontSize: 12,
    color: COLORS.darkText,
  });
  
  const metrics = [
    { label: "Members", value: "30", color: COLORS.primaryBlue },
    { label: "Regions", value: "6", color: COLORS.secondaryGold },
    { label: "Prayer Requests", value: "11", color: COLORS.green },
    { label: "Testimonies", value: "7", color: COLORS.primaryBlue },
    { label: "Activities", value: "8", color: COLORS.secondaryGold },
    { label: "Universities", value: "15+", color: COLORS.green },
    { label: "Newsletter Issues", value: "3", color: COLORS.primaryBlue },
    { label: "Gallery Photos", value: "8", color: COLORS.secondaryGold },
    { label: "Contact Messages", value: "17", color: COLORS.green },
    { label: "Chat Sessions", value: "18", color: COLORS.primaryBlue },
  ];
  
  metrics.forEach((metric, idx) => {
    const col = idx % 5;
    const row = Math.floor(idx / 5);
    
    slide.addShape(pptx.ShapeType.roundRect, {
      x: 0.3 + col * 1.94,
      y: 1.8 + row * 1.5,
      w: 1.8,
      h: 1.3,
      fill: { color: metric.color },
    });
    
    slide.addText(metric.value, {
      x: 0.3 + col * 1.94,
      y: 1.95 + row * 1.5,
      w: 1.8,
      h: 0.6,
      fontSize: 28,
      bold: true,
      color: COLORS.white,
      align: "center",
    });
    
    slide.addText(metric.label, {
      x: 0.3 + col * 1.94,
      y: 2.55 + row * 1.5,
      w: 1.8,
      h: 0.4,
      fontSize: 9,
      color: COLORS.white,
      align: "center",
    });
  });
};

// Slide 5: Visitor Analytics
const addVisitorAnalytics = (pptx: pptxgen) => {
  const slide = pptx.addSlide({ masterName: "OFUESI_MASTER" });
  
  slide.addText("Visitor Analytics (Nov 2025 - Feb 2026)", {
    x: 0.5,
    y: 0.8,
    w: 9,
    h: 0.5,
    fontSize: 28,
    bold: true,
    color: COLORS.primaryBlue,
  });

  // Summary stat boxes
  const summaryStats = [
    { label: "Total Visitors", value: "649", color: COLORS.primaryBlue },
    { label: "Page Views", value: "1,305", color: COLORS.green },
    { label: "Pages/Visit", value: "2.0", color: COLORS.secondaryGold },
  ];

  summaryStats.forEach((stat, idx) => {
    slide.addShape(pptx.ShapeType.roundRect, {
      x: 0.5 + idx * 3.2,
      y: 1.4,
      w: 2.9,
      h: 0.9,
      fill: { color: stat.color },
    });

    slide.addText(stat.value, {
      x: 0.5 + idx * 3.2,
      y: 1.42,
      w: 2.9,
      h: 0.55,
      fontSize: 28,
      bold: true,
      color: COLORS.white,
      align: "center",
    });

    slide.addText(stat.label, {
      x: 0.5 + idx * 3.2,
      y: 1.95,
      w: 2.9,
      h: 0.3,
      fontSize: 10,
      color: COLORS.white,
      align: "center",
    });
  });

  // Monthly bar chart
  const chartData = [
    {
      name: "Visitors",
      labels: ["Nov 2025", "Dec 2025", "Jan 2026", "Feb 2026*"],
      values: [56, 208, 166, 86],
    },
    {
      name: "Page Views",
      labels: ["Nov 2025", "Dec 2025", "Jan 2026", "Feb 2026*"],
      values: [114, 449, 284, 174],
    },
  ];

  slide.addChart(pptx.ChartType.bar, chartData, {
    x: 0.5,
    y: 2.5,
    w: 9,
    h: 2.4,
    barDir: "col",
    barGrouping: "clustered",
    chartColors: [COLORS.primaryBlue, COLORS.green],
    showLegend: true,
    legendPos: "b",
    showValue: true,
    dataLabelPosition: "outEnd",
    dataLabelFontSize: 9,
    catAxisTitle: "",
    valAxisTitle: "",
  });

  slide.addText("* Feb 2026 data through Feb 12 | Source: Lovable Cloud Analytics", {
    x: 0.5,
    y: 5,
    w: 9,
    h: 0.3,
    fontSize: 9,
    italic: true,
    color: COLORS.darkText,
    align: "center",
  });
};

// Slide 6: Development Cost Comparison
const addDevelopmentCostComparison = (pptx: pptxgen) => {
  const slide = pptx.addSlide({ masterName: "OFUESI_MASTER" });
  
  slide.addText("Development Cost Comparison", {
    x: 0.5,
    y: 0.8,
    w: 9,
    h: 0.5,
    fontSize: 28,
    bold: true,
    color: COLORS.primaryBlue,
  });
  
  const tableData = [
    ["Feature", "WordPress + GoDaddy", "OFUESI (Lovable)"],
    ["Custom Website Design", "$3,000 - $8,000", "Included"],
    ["Member Management System", "$2,000 - $5,000", "Included"],
    ["Admin Dashboard (34 components)", "$3,000 - $8,000", "Included"],
    ["Email Notification System", "$500 - $1,500", "Included"],
    ["AI Chatbot Integration", "$2,000 - $5,000", "Included"],
    ["Prayer Request System", "$1,000 - $2,500", "Included"],
    ["Interactive Maps", "$1,500 - $3,000", "Included"],
    ["Total Development Cost", "$13,000 - $33,000", "$1,000"],
  ];
  
  slide.addTable(tableData, {
    x: 0.3,
    y: 1.4,
    w: 9.4,
    colW: [3.5, 3, 2.9],
    border: { type: "solid", pt: 1, color: COLORS.primaryBlue },
    fill: { color: COLORS.white },
    fontFace: "Arial",
    fontSize: 11,
    color: COLORS.darkText,
    valign: "middle",
    align: "center",
    rowH: 0.4,
  });
  
  // Highlight header row
  slide.addShape(pptx.ShapeType.rect, {
    x: 0.3,
    y: 1.4,
    w: 9.4,
    h: 0.4,
    fill: { color: COLORS.primaryBlue },
  });
  
  slide.addText(["Feature", "WordPress + GoDaddy", "OFUESI (Lovable)"].join("          "), {
    x: 0.3,
    y: 1.4,
    w: 9.4,
    h: 0.4,
    fontSize: 11,
    bold: true,
    color: COLORS.white,
    align: "center",
    valign: "middle",
  });
  
  // Highlight total row
  slide.addShape(pptx.ShapeType.rect, {
    x: 0.3,
    y: 4.6,
    w: 9.4,
    h: 0.4,
    fill: { color: COLORS.secondaryGold },
  });
  
  slide.addText("💰 Savings: $12,000 - $32,000", {
    x: 0.5,
    y: 5.1,
    w: 9,
    h: 0.4,
    fontSize: 16,
    bold: true,
    color: COLORS.green,
    align: "center",
  });
};

// Slide 6: Monthly Hosting Comparison
const addMonthlyHostingComparison = (pptx: pptxgen) => {
  const slide = pptx.addSlide({ masterName: "OFUESI_MASTER" });
  
  slide.addText("Monthly Hosting Cost Comparison", {
    x: 0.5,
    y: 0.8,
    w: 9,
    h: 0.5,
    fontSize: 28,
    bold: true,
    color: COLORS.primaryBlue,
  });
  
  const tableData = [
    ["Cost Item", "WordPress + GoDaddy", "OFUESI Current"],
    ["Web Hosting", "$15 - $30/mo", "$5/mo (Ionos)"],
    ["Database Hosting", "$10 - $25/mo", "$0 (Supabase Free)"],
    ["SSL Certificate", "$0 - $10/mo", "Included"],
    ["Email Service", "$5 - $15/mo", "Included"],
    ["CDN (Content Delivery)", "$10 - $20/mo", "Included"],
    ["Plugin Subscriptions", "$20 - $50/mo", "N/A"],
    ["Backup Service", "$5 - $15/mo", "Included"],
    ["Total Monthly", "$65 - $165/mo", "$100/mo*"],
  ];
  
  slide.addTable(tableData, {
    x: 0.5,
    y: 1.4,
    w: 9,
    colW: [3.5, 2.75, 2.75],
    border: { type: "solid", pt: 1, color: COLORS.primaryBlue },
    fill: { color: COLORS.white },
    fontFace: "Arial",
    fontSize: 11,
    color: COLORS.darkText,
    valign: "middle",
    align: "center",
    rowH: 0.4,
  });
  
  slide.addText("* Includes development support and maintenance", {
    x: 0.5,
    y: 5,
    w: 9,
    h: 0.3,
    fontSize: 10,
    italic: true,
    color: COLORS.darkText,
  });
  
  // Annual comparison
  slide.addShape(pptx.ShapeType.roundRect, {
    x: 0.5,
    y: 4.5,
    w: 4.3,
    h: 0.45,
    fill: { color: COLORS.red },
  });
  
  slide.addText("WordPress Annual: $780 - $1,980", {
    x: 0.5,
    y: 4.5,
    w: 4.3,
    h: 0.45,
    fontSize: 12,
    bold: true,
    color: COLORS.white,
    align: "center",
    valign: "middle",
  });
  
  slide.addShape(pptx.ShapeType.roundRect, {
    x: 5.2,
    y: 4.5,
    w: 4.3,
    h: 0.45,
    fill: { color: COLORS.green },
  });
  
  slide.addText("OFUESI Annual: $1,200", {
    x: 5.2,
    y: 4.5,
    w: 4.3,
    h: 0.45,
    fontSize: 12,
    bold: true,
    color: COLORS.white,
    align: "center",
    valign: "middle",
  });
};

// Slide 7: Feature Comparison Matrix
const addFeatureComparison = (pptx: pptxgen) => {
  const slide = pptx.addSlide({ masterName: "OFUESI_MASTER" });
  
  slide.addText("Feature Comparison Matrix", {
    x: 0.5,
    y: 0.8,
    w: 9,
    h: 0.5,
    fontSize: 28,
    bold: true,
    color: COLORS.primaryBlue,
  });
  
  const features = [
    ["Feature", "WordPress", "GoDaddy", "OFUESI"],
    ["Custom Responsive Design", "Plugin", "Template", "✓ Custom"],
    ["Member Database Management", "Plugin ($)", "✗", "✓ Built-in"],
    ["Prayer Request System", "✗", "✗", "✓ Built-in"],
    ["AI Chatbot Assistant", "Plugin ($$$)", "✗", "✓ Built-in"],
    ["Newsletter Management", "Plugin ($)", "Basic", "✓ Built-in"],
    ["Photo Gallery", "Plugin", "Basic", "✓ Built-in"],
    ["Regional Admin Dashboards", "✗", "✗", "✓ Built-in"],
    ["Interactive USA Map", "Plugin ($)", "✗", "✓ Built-in"],
    ["Real-time Notifications", "Plugin ($)", "✗", "✓ Built-in"],
    ["Multi-role Access Control", "Plugin ($)", "Limited", "✓ 6 Roles"],
  ];
  
  slide.addTable(features, {
    x: 0.3,
    y: 1.3,
    w: 9.4,
    colW: [3.5, 2, 2, 1.9],
    border: { type: "solid", pt: 0.5, color: COLORS.primaryBlue },
    fontFace: "Arial",
    fontSize: 10,
    color: COLORS.darkText,
    valign: "middle",
    align: "center",
    rowH: 0.38,
  });
};

// Slide 8: Security Features Comparison
const addSecurityComparison = (pptx: pptxgen) => {
  const slide = pptx.addSlide({ masterName: "OFUESI_MASTER" });
  
  slide.addText("Security Features Comparison", {
    x: 0.5,
    y: 0.8,
    w: 9,
    h: 0.5,
    fontSize: 28,
    bold: true,
    color: COLORS.primaryBlue,
  });
  
  const securityFeatures = [
    ["Security Feature", "WordPress", "GoDaddy", "OFUESI"],
    ["Row Level Security (RLS)", "✗ Not Available", "✗ Not Available", "✓ All 36 Tables"],
    ["OTP Authentication", "Plugin ($50-100/yr)", "Basic Only", "✓ Custom Edge Fn"],
    ["Role-Based Access Control", "Plugin Required", "Limited", "✓ 6 Role Types"],
    ["Audit Logging", "Plugin ($100+/yr)", "✗ Not Available", "✓ Complete Trail"],
    ["Session Validation", "Basic", "Basic", "✓ Edge Function"],
    ["Password Hashing", "Plugin Dependent", "Standard", "✓ Supabase Auth"],
    ["SQL Injection Protection", "Plugin Dependent", "Limited", "✓ RLS + Params"],
    ["CORS Protection", "Manual Config", "Automatic", "✓ Edge Functions"],
    ["Two-Factor Auth Ready", "Plugin ($)", "✗", "✓ OTP System"],
  ];
  
  slide.addTable(securityFeatures, {
    x: 0.2,
    y: 1.3,
    w: 9.6,
    colW: [3.2, 2.2, 2.1, 2.1],
    border: { type: "solid", pt: 0.5, color: COLORS.primaryBlue },
    fontFace: "Arial",
    fontSize: 9.5,
    color: COLORS.darkText,
    valign: "middle",
    align: "center",
    rowH: 0.4,
  });
  
  slide.addText("🔒 OFUESI implements enterprise-grade security at no additional cost", {
    x: 0.5,
    y: 5.1,
    w: 9,
    h: 0.3,
    fontSize: 12,
    bold: true,
    color: COLORS.green,
    align: "center",
  });
};

// Slide 9: Public Pages Delivered
const addPublicPages = (pptx: pptxgen) => {
  const slide = pptx.addSlide({ masterName: "OFUESI_MASTER" });
  
  slide.addText("Public Pages Delivered (16 Pages)", {
    x: 0.5,
    y: 0.8,
    w: 9,
    h: 0.5,
    fontSize: 28,
    bold: true,
    color: COLORS.primaryBlue,
  });
  
  const pages = [
    "🏠 Home Page - Hero, stats, featured content",
    "ℹ️ About Us - Mission, vision, leadership",
    "🗺️ Regions - 6 regional pages with maps",
    "🙏 Prayer Map - Interactive prayer requests",
    "📅 Conferences - Event listings & registration",
    "📞 Contact - Multi-region contact forms",
    "💝 Give/Donate - Payment integration",
    "👩 Women's Ministry - Dedicated section",
    "🎓 New Students - Onboarding resources",
    "📰 Keep In Touch - Newsletter signup",
    "🖼️ Gallery - Photo album management",
    "🔐 Admin Login - Secure authentication",
    "📊 Admin Dashboard - Full management",
    "📄 Conference Details - Dynamic pages",
    "🏛️ Individual Region Pages - 6 regions",
    "❌ 404 Not Found - Custom error page",
  ];
  
  const col1 = pages.slice(0, 8);
  const col2 = pages.slice(8);
  
  col1.forEach((page, idx) => {
    slide.addText(page, {
      x: 0.4,
      y: 1.4 + idx * 0.45,
      w: 4.5,
      h: 0.4,
      fontSize: 10,
      color: COLORS.darkText,
    });
  });
  
  col2.forEach((page, idx) => {
    slide.addText(page, {
      x: 5.1,
      y: 1.4 + idx * 0.45,
      w: 4.5,
      h: 0.4,
      fontSize: 10,
      color: COLORS.darkText,
    });
  });
};

// Slide 10: Backend Infrastructure
const addBackendInfrastructure = (pptx: pptxgen) => {
  const slide = pptx.addSlide({ masterName: "OFUESI_MASTER" });
  
  slide.addText("Backend Infrastructure (18 Edge Functions)", {
    x: 0.5,
    y: 0.8,
    w: 9,
    h: 0.5,
    fontSize: 28,
    bold: true,
    color: COLORS.primaryBlue,
  });
  
  const categories = [
    {
      title: "🔐 Authentication (6)",
      items: ["admin-auth", "send-login-otp", "verify-login-otp", "validate-session", "admin-forgot-password", "reset-password-with-otp"],
    },
    {
      title: "👤 User Management (3)",
      items: ["create-admin-user", "delete-admin-user", "admin-password-manager"],
    },
    {
      title: "📧 Notifications (6)",
      items: ["send-contact-notifications", "send-prayer-notification", "send-testimony-notification", "send-article-notification", "send-new-student-inquiry", "send-message-reply"],
    },
    {
      title: "🤖 AI & Other (3)",
      items: ["ai-proofread", "admin-welcome-email", "admin-reset-password"],
    },
  ];
  
  categories.forEach((cat, idx) => {
    const col = idx % 2;
    const row = Math.floor(idx / 2);
    
    slide.addText(cat.title, {
      x: 0.4 + col * 4.8,
      y: 1.4 + row * 1.9,
      w: 4.5,
      h: 0.35,
      fontSize: 12,
      bold: true,
      color: COLORS.primaryBlue,
    });
    
    cat.items.forEach((item, i) => {
      slide.addText(`• ${item}`, {
        x: 0.5 + col * 4.8,
        y: 1.75 + row * 1.9 + i * 0.25,
        w: 4.4,
        h: 0.25,
        fontSize: 9,
        color: COLORS.darkText,
      });
    });
  });
};

// Slide 11: Admin Dashboard Features
const addAdminDashboard = (pptx: pptxgen) => {
  const slide = pptx.addSlide({ masterName: "OFUESI_MASTER" });
  
  slide.addText("Admin Dashboard (34 Components)", {
    x: 0.5,
    y: 0.8,
    w: 9,
    h: 0.5,
    fontSize: 28,
    bold: true,
    color: COLORS.primaryBlue,
  });
  
  const features = [
    { category: "📊 Analytics", items: ["Real-time Stats", "Chat Analytics", "Regional Dashboard", "Audit Logs"] },
    { category: "👥 Management", items: ["Member Manager", "User Management", "Activity Manager", "Gallery Manager"] },
    { category: "📝 Content", items: ["Articles Manager", "Newsletters", "Testimonies", "Prayer Requests"] },
    { category: "⚙️ Settings", items: ["Region Settings", "Password Manager", "Media Manager", "Messages"] },
  ];
  
  features.forEach((feat, idx) => {
    const col = idx % 2;
    const row = Math.floor(idx / 2);
    
    slide.addShape(pptx.ShapeType.roundRect, {
      x: 0.4 + col * 4.8,
      y: 1.4 + row * 1.8,
      w: 4.5,
      h: 1.6,
      fill: { color: COLORS.lightGray },
      line: { color: COLORS.primaryBlue, width: 1 },
    });
    
    slide.addText(feat.category, {
      x: 0.5 + col * 4.8,
      y: 1.5 + row * 1.8,
      w: 4.3,
      h: 0.35,
      fontSize: 13,
      bold: true,
      color: COLORS.primaryBlue,
    });
    
    feat.items.forEach((item, i) => {
      slide.addText(`✓ ${item}`, {
        x: 0.6 + col * 4.8,
        y: 1.85 + row * 1.8 + i * 0.28,
        w: 4.2,
        h: 0.28,
        fontSize: 10,
        color: COLORS.darkText,
      });
    });
  });
  
  slide.addText("Role-based access: Global Admin, President, Regional Admin, KIT Admin, Admin, Member", {
    x: 0.5,
    y: 5,
    w: 9,
    h: 0.3,
    fontSize: 10,
    italic: true,
    color: COLORS.primaryBlue,
    align: "center",
  });
};

// Slide 12: Visibility Improvements
const addVisibilityImprovements = (pptx: pptxgen) => {
  const slide = pptx.addSlide({ masterName: "OFUESI_MASTER" });
  
  slide.addText("Visibility & Reachability Improvements", {
    x: 0.5,
    y: 0.8,
    w: 9,
    h: 0.5,
    fontSize: 28,
    bold: true,
    color: COLORS.primaryBlue,
  });
  
  const improvements = [
    ["Metric", "Before", "After", "Improvement"],
    ["Online Presence", "Limited", "Full Website", "↑ 100%"],
    ["Member Directory", "Spreadsheets", "Searchable DB", "↑ Efficiency"],
    ["Prayer Requests", "Email Only", "Online System", "↑ Engagement"],
    ["Regional Visibility", "None", "6 Region Pages", "↑ Organization"],
    ["Event Management", "Manual", "Automated", "↑ Attendance"],
    ["Newsletter Access", "Email Only", "Web Archive", "↑ Reach"],
    ["New Student Outreach", "Word of Mouth", "Dedicated Page", "↑ Growth"],
  ];
  
  slide.addTable(improvements, {
    x: 0.5,
    y: 1.4,
    w: 9,
    colW: [2.8, 2, 2.2, 2],
    border: { type: "solid", pt: 0.5, color: COLORS.primaryBlue },
    fontFace: "Arial",
    fontSize: 11,
    color: COLORS.darkText,
    valign: "middle",
    align: "center",
    rowH: 0.45,
  });
};

// Slide 13: Technical Stack
const addTechnicalStack = (pptx: pptxgen) => {
  const slide = pptx.addSlide({ masterName: "OFUESI_MASTER" });
  
  slide.addText("Technical Stack", {
    x: 0.5,
    y: 0.8,
    w: 9,
    h: 0.5,
    fontSize: 28,
    bold: true,
    color: COLORS.primaryBlue,
  });
  
  const stack = [
    { category: "Frontend", tech: "React 18, TypeScript, Vite", desc: "Modern, fast, type-safe" },
    { category: "Styling", tech: "Tailwind CSS, shadcn/ui", desc: "45+ UI components" },
    { category: "Animation", tech: "Framer Motion", desc: "Smooth page transitions" },
    { category: "Database", tech: "Supabase (PostgreSQL)", desc: "36 tables, real-time" },
    { category: "Auth", tech: "Supabase Auth + Custom OTP", desc: "Enterprise security" },
    { category: "Backend", tech: "Supabase Edge Functions", desc: "18 serverless functions" },
    { category: "Maps", tech: "react-simple-maps, D3.js", desc: "Interactive USA maps" },
    { category: "Hosting", tech: "Ionos + Supabase Cloud", desc: "Global CDN delivery" },
  ];
  
  stack.forEach((item, idx) => {
    const col = idx % 2;
    const row = Math.floor(idx / 2);
    
    slide.addShape(pptx.ShapeType.roundRect, {
      x: 0.4 + col * 4.8,
      y: 1.4 + row * 0.95,
      w: 4.5,
      h: 0.85,
      fill: { color: col === 0 ? COLORS.primaryBlue : COLORS.secondaryGold },
    });
    
    slide.addText(item.category, {
      x: 0.5 + col * 4.8,
      y: 1.45 + row * 0.95,
      w: 4.3,
      h: 0.25,
      fontSize: 10,
      bold: true,
      color: COLORS.white,
    });
    
    slide.addText(item.tech, {
      x: 0.5 + col * 4.8,
      y: 1.7 + row * 0.95,
      w: 4.3,
      h: 0.25,
      fontSize: 11,
      color: COLORS.white,
    });
    
    slide.addText(item.desc, {
      x: 0.5 + col * 4.8,
      y: 1.95 + row * 0.95,
      w: 4.3,
      h: 0.2,
      fontSize: 8,
      italic: true,
      color: COLORS.white,
    });
  });
};

// Slide 14: 5-Year Cost Projection
const addCostProjection = (pptx: pptxgen) => {
  const slide = pptx.addSlide({ masterName: "OFUESI_MASTER" });
  
  slide.addText("5-Year Cost Projection", {
    x: 0.5,
    y: 0.8,
    w: 9,
    h: 0.5,
    fontSize: 28,
    bold: true,
    color: COLORS.primaryBlue,
  });
  
  // Chart data
  const chartData = [
    {
      name: "WordPress + GoDaddy",
      labels: ["Year 1", "Year 2", "Year 3", "Year 4", "Year 5"],
      values: [14260, 15520, 16780, 18040, 19300], // Dev + hosting cumulative
    },
    {
      name: "OFUESI (Current)",
      labels: ["Year 1", "Year 2", "Year 3", "Year 4", "Year 5"],
      values: [2200, 3400, 4600, 5800, 7000], // $1000 dev + $100/mo
    },
  ];
  
  slide.addChart(pptx.ChartType.bar, chartData, {
    x: 0.5,
    y: 1.4,
    w: 9,
    h: 3.2,
    barDir: "bar",
    barGrouping: "clustered",
    chartColors: [COLORS.red, COLORS.green],
    showLegend: true,
    legendPos: "b",
    showValue: true,
    dataLabelPosition: "outEnd",
    dataLabelFontSize: 9,
    catAxisTitle: "",
    valAxisTitle: "Total Cost ($)",
    valAxisMaxVal: 25000,
  });
  
  slide.addText("💰 5-Year Savings: $7,260 - $12,300", {
    x: 0.5,
    y: 4.8,
    w: 9,
    h: 0.4,
    fontSize: 16,
    bold: true,
    color: COLORS.green,
    align: "center",
  });
};

// Slide 15: ROI Summary
const addROISummary = (pptx: pptxgen) => {
  const slide = pptx.addSlide({ masterName: "OFUESI_MASTER" });
  
  slide.addText("Return on Investment Summary", {
    x: 0.5,
    y: 0.8,
    w: 9,
    h: 0.5,
    fontSize: 28,
    bold: true,
    color: COLORS.primaryBlue,
  });
  
  // Investment box
  slide.addShape(pptx.ShapeType.roundRect, {
    x: 0.5,
    y: 1.4,
    w: 4.3,
    h: 1.2,
    fill: { color: COLORS.primaryBlue },
  });
  
  slide.addText("Total Investment", {
    x: 0.5,
    y: 1.5,
    w: 4.3,
    h: 0.3,
    fontSize: 14,
    color: COLORS.white,
    align: "center",
  });
  
  slide.addText("$1,000 + $100/mo", {
    x: 0.5,
    y: 1.85,
    w: 4.3,
    h: 0.5,
    fontSize: 24,
    bold: true,
    color: COLORS.secondaryGold,
    align: "center",
  });
  
  // Market value box
  slide.addShape(pptx.ShapeType.roundRect, {
    x: 5.2,
    y: 1.4,
    w: 4.3,
    h: 1.2,
    fill: { color: COLORS.green },
  });
  
  slide.addText("Market Value", {
    x: 5.2,
    y: 1.5,
    w: 4.3,
    h: 0.3,
    fontSize: 14,
    color: COLORS.white,
    align: "center",
  });
  
  slide.addText("$13,000 - $33,000", {
    x: 5.2,
    y: 1.85,
    w: 4.3,
    h: 0.5,
    fontSize: 24,
    bold: true,
    color: COLORS.white,
    align: "center",
  });
  
  // Key benefits
  slide.addText("Key Benefits Delivered:", {
    x: 0.5,
    y: 2.8,
    w: 9,
    h: 0.35,
    fontSize: 14,
    bold: true,
    color: COLORS.primaryBlue,
  });
  
  const benefits = [
    "✓ Complete custom website with 16 public pages",
    "✓ Enterprise-grade security (RLS, OTP, RBAC)",
    "✓ 34-component admin dashboard with 6 user roles",
    "✓ AI-powered chatbot and content proofreading",
    "✓ Real-time database with 36 tables",
    "✓ 18 serverless backend functions",
    "✓ 514 visitors with 1,054 page views since Nov 2025",
    "✓ 17 contact messages & 18 chatbot sessions",
  ];
  
  benefits.forEach((benefit, idx) => {
    const col = idx % 2;
    const row = Math.floor(idx / 2);
    
    slide.addText(benefit, {
      x: 0.5 + col * 4.7,
      y: 3.2 + row * 0.35,
      w: 4.5,
      h: 0.35,
      fontSize: 11,
      color: COLORS.darkText,
    });
  });
  
  // Contact info
  slide.addShape(pptx.ShapeType.roundRect, {
    x: 0.5,
    y: 4.4,
    w: 9,
    h: 0.7,
    fill: { color: COLORS.lightGray },
    line: { color: COLORS.secondaryGold, width: 2 },
  });
  
  slide.addText("For questions or additional information, please contact: ofuesi.website@gmail.com", {
    x: 0.5,
    y: 4.55,
    w: 9,
    h: 0.4,
    fontSize: 12,
    color: COLORS.primaryBlue,
    align: "center",
  });
};

// Main export function
export async function generateProjectReport(): Promise<void> {
  const pptx = new pptxgen();
  
  // Set presentation properties
  pptx.author = "OFUESI";
  pptx.title = "OFUESI Website Project Report";
  pptx.subject = "Development Cost & Platform Comparison";
  pptx.company = "Overseas Fellowship of University Evangelical Students of India";
  
  // Configure master slides
  configureMaster(pptx);
  
  // Add all slides
  addTitleSlide(pptx);                    // Slide 1
  addExecutiveSummary(pptx);              // Slide 2
  addProjectStatistics(pptx);             // Slide 3
  addLiveDataMetrics(pptx);               // Slide 4
  addVisitorAnalytics(pptx);              // Slide 5
  addDevelopmentCostComparison(pptx);     // Slide 6
  addMonthlyHostingComparison(pptx);      // Slide 7
  addFeatureComparison(pptx);             // Slide 8
  addSecurityComparison(pptx);            // Slide 9
  addPublicPages(pptx);                   // Slide 10
  addBackendInfrastructure(pptx);         // Slide 11
  addAdminDashboard(pptx);                // Slide 12
  addVisibilityImprovements(pptx);        // Slide 13
  addTechnicalStack(pptx);                // Slide 14
  addCostProjection(pptx);                // Slide 15
  addROISummary(pptx);                    // Slide 16
  
  // Generate filename with date
  const today = new Date().toISOString().split("T")[0];
  const fileName = `OFUESI-Project-Report-${today}.pptx`;
  
  // Download the file
  await pptx.writeFile({ fileName });
}

export default generateProjectReport;
